import { createLogger, createStore } from "vuex";
import users from "./modules/users";
import device from "./modules/device";
import { ownerFilter, parseQuery, getQuickFilters } from "@/lib/search";
import {
  queryTenants,
  createTenant,
  inviteUser,
  updateUser,
  removeUser,
  removeUserTenant,
  queryTenantDevices,
} from "@/api/tenants";
import auth from "@/store/modules/auth";

const debug = process.env.NODE_ENV !== "production";

const notificationTimeout = 10; //seconds

const state = () => ({
  searchQueryData: {},
  searchQuery: "",
  quickFilters: getQuickFilters(),
  ownerFilters: [],
  notification: null,
  allTenants: null,
  tenants: null,
  loading: false,
  loadingTenants: false,
  loadingUsers: false,
  loadingDevices: false,
  hasNextPage: false,
  nextPageToken: "",
});

const actions = {
  async search({ commit, state }, searchQuery) {
    commit("setSearchQuery", searchQuery);
    commit("setSearchQueryData", parseQuery(searchQuery, state.allTenants));
  },

  async addFilterToSearch({ commit, state }, filter) {
    if (filter === "") {
      return actions.search({ commit, state }, "");
    }
    if (state.searchQuery) {
      const filterType = filter.split(":")[0];
      const queryTerms = state.searchQuery.split(" ");

      if (queryTerms.find((term) => term.startsWith(filterType))) {
        filter = queryTerms.map((term) => (term.startsWith(filterType) ? filter : term)).join(" ");
      } else {
        filter = queryTerms.concat([filter]).join(" ");
      }
    }
    return actions.search({ commit, state }, filter);
  },

  async setQuickFilterStats({ commit }, quickFilterWithStats) {
    commit("setQuickFilter", quickFilterWithStats);
  },

  async refreshQuickFilters({ commit }) {
    try {
      // Regenerate quickFilters with current language translations
      const freshFilters = getQuickFilters();
      commit("setQuickFilter", freshFilters);
    } catch (error) {
      console.error("Error refreshing quick filters:", error);
      throw error;
    }
  },

  async loadAllTenants({ commit, state }, refresh = false) {
    const cacheKey = "allTenants";
    const cacheExpiry = 5 * 60 * 1000; // 5 minutes

    if (!refresh) {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { tenants, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < cacheExpiry) {
            commit("setAllTenants", tenants);
            return;
          }
        }
      } catch (e) {
        console.warn("Failed to load tenants from cache", e);
      }
    }

    if (!state.allTenants || refresh) {
      commit("setLoadingTenants", true);

      let hasNextPage = true;
      let nextPageToken = null;
      let tenants = [];
      // TODO check! do we need all tenants all the time?
      while (hasNextPage) {
        const result = await queryTenants(nextPageToken, 100);
        hasNextPage = result.hasNextPage;
        nextPageToken = result.nextPageToken;
        if (!result.tenants || result.tenants.length === 0) {
          break;
        }
        tenants = tenants.concat(result.tenants);
        if (tenants.length > 2) tenants.sort((a, b) => a.name.localeCompare(b.name));
        commit("setAllTenants", tenants);
      }

      try {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            tenants,
            timestamp: Date.now(),
          })
        );
      } catch (e) {
        console.warn("Failed to cache tenants", e);
      }

      commit("setLoadingTenants", false);
    }
  },

  async loadTenants({ commit, state }, refresh = false) {
    if (refresh) {
      commit("setHasNextPage", false);
      commit("setNextPageToken", null);
      commit("setTenants", null);
    }

    commit("setLoading", true);

    let hasNextPage = state.hasNextPage;
    let nextPageToken = state.nextPageToken ? state.nextPageToken : null;
    const result = await queryTenants(nextPageToken, hasNextPage ? 30 : 60);

    hasNextPage = result.hasNextPage;
    nextPageToken = result.nextPageToken;

    let tenants = [];
    if (state.tenants && state.tenants.length > 0) {
      tenants = Array.from(state.tenants).concat(result.tenants);
    }

    commit("setHasNextPage", hasNextPage);
    commit("setNextPageToken", nextPageToken);
    commit("setTenants", tenants.length > 0 ? tenants : result.tenants);
    commit("setLoading", false);
  },

  async loadTenantDevices({ commit, state }, tenantId) {
    if (tenantId) {
      commit("setLoadingTenants", true);

      let hasNextPage = true;
      let nextPageToken = null;
      let devices = [];

      while (hasNextPage) {
        const result = await queryTenantDevices(nextPageToken, tenantId);
        hasNextPage = result.hasNextPage;
        nextPageToken = result.nextPageToken;

        devices = devices.concat(
          result.tenantDevices
            ? result.tenantDevices[0].map((node) => {
                return node.node;
              })
            : []
        );
      }

      const updatedTenants = state.tenants.map((tenant) => {
        if (tenant.id === tenantId) {
          return { ...tenant, devices };
        }
        return tenant;
      });

      commit("setTenants", updatedTenants);
      commit("setLoadingTenants", false);
    }
  },

  async createOwnerFilters({ commit, state }) {
    commit(
      "setOwnerFilters",
      state.allTenants.map((tenant) => ownerFilter(tenant))
    );
  },

  async showNotification({ commit }, notification) {
    commit("clearNotification", notification);

    notification.timeoutId = setTimeout(() => {
      commit("clearNotification");
    }, notificationTimeout * 1000);

    commit("showNotification", notification);
  },

  async createTenant({ commit }, tenantName) {
    commit("setLoading", true);
    const result = await createTenant(tenantName);
    commit("setLoading", false);
    return result;
  },

  async inviteTenantUser({ commit }, data) {
    commit("setLoading", true);
    const result = await inviteUser(data);
    commit("setLoading", false);
    return result;
  },

  async inviteUsersForTenant({ commit }, data) {
    commit("setLoading", true);
    data.emails.forEach(async (user) => {
      await inviteUser({
        email: user.mail,
        tenantsRoles: [
          {
            tenantId: data.tenantId,
            role: user.role,
          },
        ],
      });
    });
    commit("setLoading", false);
  },

  async updateTenantUser({ commit }, data) {
    commit("setLoading", true);
    const result = await updateUser(data);
    commit("setLoading", false);
    return result;
  },

  async removeTenantUser({ commit }, data) {
    commit("setLoading", true);
    const result = await removeUser(data);
    commit("setLoading", false);
    return result;
  },

  async removeUserFromTenant({ commit }, data) {
    commit("setLoading", true);
    const result = await removeUserTenant(data);
    commit("setLoading", false);
    return result;
  },

  async removeUsersFromTenant({ commit }, data) {
    commit("setLoading", true);
    data.emails.forEach(async (user) => {
      await removeUserTenant({
        userId: user.id,
        tenantsIds: [data.tenantId],
      });
    });
    commit("setLoading", false);
  },

  async clearTenantsCache({ commit }) {
    localStorage.removeItem("allTenants");
    commit("setAllTenants", null);
  },
};

const mutations = {
  setLoading(state, loading) {
    state.loading = loading;
  },
  setLoadingTenants(state, loading) {
    state.loadingTenants = loading;
  },
  setAllTenants(state, tenants) {
    state.allTenants = tenants;
  },
  setTenants(state, tenants) {
    state.tenants = tenants;
  },
  setQuickFilter(state, filter) {
    state.quickFilters = filter;
  },
  setSearchQueryData(state, searchQueryData) {
    state.searchQueryData = searchQueryData;
  },
  setSearchQuery(state, searchQuery) {
    state.searchQuery = searchQuery;
  },
  setOwnerFilters(state, ownerFilters) {
    state.ownerFilters = ownerFilters;
  },
  showNotification(state, notification) {
    state.notification = notification;
  },
  clearNotification(state) {
    if (state.notification && state.notification.timeoutId) {
      clearInterval(state.notification.timeoutId);
    }
    state.notification = null;
  },
  setHasNextPage(state, nextPage) {
    state.hasNextPage = nextPage;
  },
  setNextPageToken(state, pageToken) {
    state.nextPageToken = pageToken;
  },
};

export default createStore({
  modules: {
    users,
    device,
    auth,
  },
  state,
  actions,
  mutations,
  strict: debug,
  plugins: debug ? [createLogger()] : [],
});
